// Security utilities for portfolio protection

interface SecurityConfig {
  maxAttempts: number
  lockoutDuration: number
  sessionTimeout: number
  allowedOrigins: string[]
  rateLimitWindow: number
  rateLimitMax: number
}

const SECURITY_CONFIG: SecurityConfig = {
  maxAttempts: 10, // Increased for better user experience
  lockoutDuration: 5 * 60 * 1000, // Reduced to 5 minutes
  sessionTimeout: 2 * 60 * 1000, // 2 minutes
  allowedOrigins: [
    'localhost', '127.0.0.1', '::1', '0.0.0.0', // Development origins
    'netlify.app', 'netlify.com', // Netlify domains
    'github.io', 'github.com', // GitHub Pages
    'vercel.app', 'vercel.com', // Vercel domains
    'herokuapp.com', // Heroku domains
    'firebaseapp.com', // Firebase domains
    'surge.sh', // Surge domains
    'now.sh' // Vercel legacy domains
  ],
  rateLimitWindow: 60 * 1000, // 1 minute
  rateLimitMax: 20 // Increased for better user experience
}

interface AttemptRecord {
  count: number
  lastAttempt: number
  lockedUntil?: number
}

interface RateLimitRecord {
  requests: number[]
  windowStart: number
}

class SecurityManager {
  private static instance: SecurityManager
  private attempts: Map<string, AttemptRecord> = new Map()
  private rateLimits: Map<string, RateLimitRecord> = new Map()
  private sessionStartTime: number = 0
  private sessionId: string = ''

  static getInstance(): SecurityManager {
    if (!SecurityManager.instance) {
      SecurityManager.instance = new SecurityManager()
    }
    return SecurityManager.instance
  }

  // Generate secure session ID
  generateSessionId(): string {
    const timestamp = Date.now().toString(36)
    const random = Math.random().toString(36).substring(2)
    const fingerprint = this.getBrowserFingerprint()
    return `${timestamp}-${random}-${fingerprint}`
  }

  // Get browser fingerprint for additional security
  private getBrowserFingerprint(): string {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (ctx) {
      ctx.textBaseline = 'top'
      ctx.font = '14px Arial'
      ctx.fillText('Browser fingerprint', 2, 2)
    }
    
    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      new Date().getTimezoneOffset(),
      canvas.toDataURL()
    ].join('|')
    
    // Simple hash function
    let hash = 0
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36)
  }

  // Check if origin is allowed
  isOriginAllowed(): boolean {
    const origin = window.location.hostname
    const port = window.location.port
    const fullOrigin = window.location.origin
    
    console.log('Security check - Origin:', origin, 'Full origin:', fullOrigin)
    
    // Allow all localhost variations for development
    if (origin === 'localhost' || origin === '127.0.0.1' || origin === '::1' || origin === '0.0.0.0') {
      console.log('Origin allowed: localhost/development')
      return true
    }
    
    // Allow any subdomain of allowed origins (e.g., mysite.netlify.app)
    const isAllowed = SECURITY_CONFIG.allowedOrigins.some(allowed => {
      // Exact match
      if (origin === allowed) {
        console.log('Origin allowed: exact match with', allowed)
        return true
      }
      
      // Subdomain match (e.g., mysite.netlify.app matches netlify.app)
      if (origin.endsWith('.' + allowed)) {
        console.log('Origin allowed: subdomain match with', allowed)
        return true
      }
      
      // Contains match for flexibility
      if (origin.includes(allowed)) {
        console.log('Origin allowed: contains match with', allowed)
        return true
      }
      
      return false
    })
    
    if (!isAllowed) {
      console.log('Origin NOT allowed:', origin, 'Allowed origins:', SECURITY_CONFIG.allowedOrigins)
    }
    
    return isAllowed
  }

  // Rate limiting
  isRateLimited(identifier: string): boolean {
    const now = Date.now()
    const record = this.rateLimits.get(identifier)
    
    if (!record) {
      this.rateLimits.set(identifier, {
        requests: [now],
        windowStart: now
      })
      return false
    }

    // Clean old requests outside the window
    const windowStart = now - SECURITY_CONFIG.rateLimitWindow
    record.requests = record.requests.filter(time => time > windowStart)
    
    if (record.requests.length >= SECURITY_CONFIG.rateLimitMax) {
      return true
    }

    record.requests.push(now)
    return false
  }

  // Track failed login attempts
  recordFailedAttempt(identifier: string): { isLocked: boolean; remainingAttempts: number } {
    const now = Date.now()
    const record = this.attempts.get(identifier) || { count: 0, lastAttempt: 0 }

    // Check if still locked
    if (record.lockedUntil && now < record.lockedUntil) {
      return { isLocked: true, remainingAttempts: 0 }
    }

    // Reset if lockout period has passed
    if (record.lockedUntil && now >= record.lockedUntil) {
      record.count = 0
      record.lockedUntil = undefined
    }

    record.count++
    record.lastAttempt = now

    // Lock if max attempts reached
    if (record.count >= SECURITY_CONFIG.maxAttempts) {
      record.lockedUntil = now + SECURITY_CONFIG.lockoutDuration
    }

    this.attempts.set(identifier, record)
    
    const remainingAttempts = Math.max(0, SECURITY_CONFIG.maxAttempts - record.count)
    return { isLocked: record.lockedUntil ? true : false, remainingAttempts }
  }

  // Clear failed attempts on successful login
  clearFailedAttempts(identifier: string): void {
    this.attempts.delete(identifier)
  }

  // Clear all failed attempts (for resetting security state)
  clearAllFailedAttempts(): void {
    this.attempts.clear()
  }

  // Session management
  startSession(): string {
    this.sessionId = this.generateSessionId()
    this.sessionStartTime = Date.now()
    
    // Store session info securely
    sessionStorage.setItem('portfolio_session_id', this.sessionId)
    sessionStorage.setItem('portfolio_session_start', this.sessionStartTime.toString())
    
    return this.sessionId
  }

  // Validate session
  isSessionValid(): boolean {
    const storedSessionId = sessionStorage.getItem('portfolio_session_id')
    const storedStartTime = sessionStorage.getItem('portfolio_session_start')
    
    if (!storedSessionId || !storedStartTime) {
      return false
    }

    const sessionAge = Date.now() - parseInt(storedStartTime)
    return sessionAge < SECURITY_CONFIG.sessionTimeout && storedSessionId === this.sessionId
  }

  // End session
  endSession(): void {
    this.sessionId = ''
    this.sessionStartTime = 0
    sessionStorage.removeItem('portfolio_session_id')
    sessionStorage.removeItem('portfolio_session_start')
  }

  // Detect suspicious activity
  detectSuspiciousActivity(): boolean {
    // Skip suspicious activity detection for development
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return false
    }

    // Check for common hacking tools
    const suspiciousPatterns = [
      /eval\s*\(/i,
      /document\.write/i,
      /innerHTML\s*=/i,
      /script\s*src/i,
      /onload\s*=/i,
      /onerror\s*=/i
    ]

    const userAgent = navigator.userAgent.toLowerCase()
    const suspiciousTools = [
      'curl', 'wget', 'python', 'bot', 'crawler', 'spider',
      'scraper', 'automation', 'selenium', 'phantom', 'headless'
    ]

    // Check for suspicious patterns in URL or user agent
    const currentUrl = window.location.href
    const hasSuspiciousPattern = suspiciousPatterns.some(pattern => 
      pattern.test(currentUrl) || pattern.test(userAgent)
    )

    const hasSuspiciousTool = suspiciousTools.some(tool => 
      userAgent.includes(tool)
    )

    return hasSuspiciousPattern || hasSuspiciousTool
  }

  // Get client IP (approximate)
  getClientIdentifier(): string {
    const fingerprint = this.getBrowserFingerprint()
    const userAgent = navigator.userAgent
    const language = navigator.language
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    
    return btoa(`${fingerprint}-${userAgent}-${language}-${timezone}`).substring(0, 16)
  }

  // Security headers check
  checkSecurityHeaders(): boolean {
    // For development, be more lenient with security headers
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return true
    }

    // Check if running in secure context (only for production)
    if (!window.isSecureContext && location.protocol !== 'https:') {
      console.warn('Not running in secure context')
      return false
    }

    // Check for common security headers (if available)
    const hasSecurityHeaders = document.referrer === '' || 
      document.referrer.includes(window.location.origin)

    return hasSecurityHeaders
  }

  // Cleanup old records
  cleanup(): void {
    const now = Date.now()
    
    // Clean old attempt records
    for (const [key, record] of this.attempts.entries()) {
      if (record.lockedUntil && now > record.lockedUntil + SECURITY_CONFIG.lockoutDuration) {
        this.attempts.delete(key)
      }
    }

    // Clean old rate limit records
    for (const [key, record] of this.rateLimits.entries()) {
      if (now - record.windowStart > SECURITY_CONFIG.rateLimitWindow * 2) {
        this.rateLimits.delete(key)
      }
    }
  }
}

// Initialize cleanup interval
const securityManager = SecurityManager.getInstance()
setInterval(() => securityManager.cleanup(), 5 * 60 * 1000) // Cleanup every 5 minutes

export default securityManager
